import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Hash, Lock, Plus, MessageCircle, Settings, LogOut, Search,
  PenSquare, X, User
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ onCreateTeam, onCreateChannel, onOpenProfile, onInvite }) => {
  const { user, token, logout } = useAuth();
  const {
    teams, channels, dms, activeChannel, activeTeam,
    setActiveChannel, setActiveTeam, onlineUsers, unreadCounts,
    loadTeams, loadChannels, loadDMs
  } = useChat();

  const [activeTab, setActiveTab] = useState('channels');

  // New DM dialog state
  const [showNewDM, setShowNewDM] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [startingDM, setStartingDM] = useState(null);

  useEffect(() => {
    loadTeams();
    loadDMs();
  }, [loadTeams, loadDMs]);

  useEffect(() => {
    if (activeTeam) {
      loadChannels(activeTeam.team_id);
    }
  }, [activeTeam, loadChannels]);

  // Fetch all users when opening new DM dialog
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers((res.data.users || []).filter(u => u.user_id !== user?.user_id));
    } catch (e) {
      console.error('Failed to fetch users', e);
    }
    setLoadingUsers(false);
  }, [token, user?.user_id]);

  useEffect(() => {
    if (showNewDM) fetchUsers();
  }, [showNewDM, fetchUsers]);

  const filteredUsers = allUsers.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (
      (u.display_name || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  });

  const handleStartDM = async (recipientId) => {
    setStartingDM(recipientId);
    try {
      const res = await axios.post(`${API}/dm`, { recipient_id: recipientId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dm = res.data.dm;
      // Reload DMs and switch to the new one
      await loadDMs();
      setActiveTab('dms');
      setShowNewDM(false);
      setUserSearch('');
      // Find the DM in the refreshed list and set it active
      // Use a small delay to ensure DMs are loaded
      setTimeout(() => {
        setActiveChannel({ ...dm, dm_id: dm.dm_id });
      }, 300);
    } catch (e) {
      console.error('Failed to start DM', e);
    }
    setStartingDM(null);
  };

  const isChannelActive = (ch) => {
    if (!activeChannel) return false;
    if (ch.channel_id) return activeChannel.channel_id === ch.channel_id;
    if (ch.dm_id) return activeChannel.dm_id === ch.dm_id;
    return false;
  };

  return (
    <div className="h-full flex flex-col relative sidebar-grain" data-testid="sidebar">
      {/* App Header */}
      <div className="px-4 pt-4 pb-2" data-testid="sidebar-header">
        <div className="flex items-center gap-2.5 mb-4">
          <img src="/imd-logo.svg" alt="IMD Logo" className="h-8 w-auto" />
          <h2 className="text-base font-bold tracking-tight" style={{fontFamily: 'Space Grotesk'}}>Tello</h2>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-[hsl(var(--surface-2))] rounded-lg p-0.5" data-testid="sidebar-tab-switcher">
          <button
            className={`flex-1 text-xs font-medium py-2 px-3 rounded-md transition-all duration-150 ${activeTab === 'channels' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('channels')}
            data-testid="sidebar-tab-channels"
          >
            <Hash className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Channels
          </button>
          <button
            className={`flex-1 text-xs font-medium py-2 px-3 rounded-md transition-all duration-150 ${activeTab === 'dms' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('dms')}
            data-testid="sidebar-tab-dms"
          >
            <MessageCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Messages
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {/* Channels Tab */}
        {activeTab === 'channels' && (
          <div className="px-2 pt-2 pb-4" data-testid="sidebar-channels-section">
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {activeTeam?.name || 'Channels'}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-[hsl(var(--surface-2))] cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={onCreateChannel}
                    data-testid="sidebar-create-channel-button"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Create Channel</TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-0.5">
              {channels.map(ch => {
                const active = isChannelActive(ch);
                const unread = unreadCounts[ch.channel_id] || 0;
                return (
                  <button
                    key={ch.channel_id}
                    className={`group w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm transition-colors duration-150 ${active ? 'bg-[hsl(var(--primary)/0.10)] text-foreground font-medium' : 'text-muted-foreground hover:bg-[hsl(var(--message-hover))] hover:text-foreground'}`}
                    onClick={() => setActiveChannel(ch)}
                    data-testid={`channel-row-${ch.channel_id}`}
                  >
                    {active && <div className="w-0.5 h-4 rounded-full bg-[hsl(var(--primary))] flex-shrink-0" />}
                    {ch.is_private ? <Lock className="w-3.5 h-3.5 flex-shrink-0 opacity-60" /> : <Hash className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />}
                    <span className={`flex-1 truncate text-left ${unread > 0 ? 'font-semibold text-foreground' : ''}`}>{ch.name}</span>
                    {unread > 0 && <Badge className="h-4.5 min-w-[18px] px-1.5 text-[10px] bg-[hsl(var(--primary))] text-white border-0">{unread > 99 ? '99+' : unread}</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* DMs Tab */}
        {activeTab === 'dms' && (
          <div className="px-2 pt-2 pb-4" data-testid="sidebar-dms-section">
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">Direct Messages</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-[hsl(var(--surface-2))] cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewDM(true)}
                    data-testid="sidebar-new-dm-button"
                  >
                    <PenSquare className="w-3.5 h-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>New Message</TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-0.5">
              {dms.map(dm => {
                const active = isChannelActive(dm);
                const other = dm.other_user;
                const isOnline = onlineUsers[other?.user_id];
                const unread = unreadCounts[dm.dm_id] || 0;
                return (
                  <button
                    key={dm.dm_id}
                    className={`group w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors duration-150 ${active ? 'bg-[hsl(var(--primary)/0.10)] text-foreground' : 'text-muted-foreground hover:bg-[hsl(var(--message-hover))] hover:text-foreground'}`}
                    onClick={() => setActiveChannel(dm)}
                    data-testid={`dm-row-${dm.dm_id}`}
                  >
                    {active && <div className="w-0.5 h-4 rounded-full bg-[hsl(var(--primary))] flex-shrink-0" />}
                    <div className="relative flex-shrink-0">
                      <div className="w-7 h-7 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold text-foreground overflow-hidden">
                        {other?.avatar ? <img src={other.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${other.avatar}` : other.avatar} alt="" className="w-full h-full object-cover" /> : (other?.display_name?.[0] || other?.username?.[0] || '?')}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 presence-dot ${isOnline ? 'presence-online' : 'presence-offline'}`} style={{width: '9px', height: '9px'}} />
                    </div>
                    <span className={`flex-1 truncate text-left ${unread > 0 ? 'font-semibold text-foreground' : ''}`}>{other?.display_name || other?.username || 'User'}</span>
                    {unread > 0 && <Badge className="h-4.5 min-w-[18px] px-1.5 text-[10px] bg-[hsl(var(--primary))] text-white border-0">{unread > 99 ? '99+' : unread}</Badge>}
                  </button>
                );
              })}
              {dms.length === 0 && (
                <div className="text-center py-6 px-4">
                  <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-xs text-muted-foreground mb-3">No conversations yet</p>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowNewDM(true)} data-testid="sidebar-start-dm-empty-btn">
                    <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Start a conversation
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </ScrollArea>

      {/* User Panel */}
      <div className="border-t border-[hsl(var(--divider))] px-3 py-3" data-testid="sidebar-user-panel">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.15)] flex items-center justify-center text-sm font-semibold text-[hsl(var(--primary))] overflow-hidden">
              {user?.avatar ? <img src={user.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar}` : user.avatar} alt="" className="w-full h-full object-cover" /> : (user?.display_name?.[0] || user?.username?.[0] || '?')}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 presence-dot presence-online" style={{width: '9px', height: '9px'}} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.display_name || user?.username}</p>
            <p className="text-xs text-muted-foreground truncate">Online</p>
          </div>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={onOpenProfile} data-testid="sidebar-user-menu-trigger">
                  <Settings className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Settings</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={logout} data-testid="sidebar-logout-button">
                  <LogOut className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sign Out</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* New DM Dialog */}
      <Dialog open={showNewDM} onOpenChange={setShowNewDM}>
        <DialogContent className="max-w-sm" data-testid="new-dm-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Space Grotesk'}}>New Message</DialogTitle>
            <DialogDescription>Search for a team member to start a conversation.</DialogDescription>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, username, or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="pl-9"
              autoFocus
              data-testid="new-dm-search-input"
            />
            {userSearch && (
              <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setUserSearch('')}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <ScrollArea className="max-h-64">
            {loadingUsers ? (
              <div className="py-6 text-center">
                <div className="w-5 h-5 border-2 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-6 text-center">
                <User className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-xs text-muted-foreground">{userSearch ? 'No users found' : 'No team members available'}</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredUsers.map(u => {
                  const isOnline = onlineUsers[u.user_id];
                  return (
                    <button
                      key={u.user_id}
                      className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-[hsl(var(--message-hover))] transition-colors duration-150 text-left"
                      onClick={() => handleStartDM(u.user_id)}
                      disabled={startingDM === u.user_id}
                      data-testid={`new-dm-user-${u.user_id}`}
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-9 h-9 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold text-foreground overflow-hidden">
                          {u.avatar ? <img src={u.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar}` : u.avatar} alt="" className="w-full h-full object-cover" /> : (u.display_name?.[0] || u.username?.[0] || '?')}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${isOnline ? 'bg-[hsl(var(--presence-online))]' : 'bg-muted-foreground/30'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{u.display_name || u.username}</p>
                        <p className="text-xs text-muted-foreground truncate">@{u.username} {u.title ? ` \u00b7 ${u.title}` : ''}</p>
                      </div>
                      {startingDM === u.user_id && (
                        <div className="w-4 h-4 border-2 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sidebar;
